// src/services/utils.js
export const formatters = {
  // Number formatting
  formatNumber(num, locale = 'en-US') {
    if (!num && num !== 0) return '0'
    return new Intl.NumberFormat(locale).format(num)
  },

  // Currency formatting
  formatCurrency(amount, currency = 'USD', locale = 'en-US') {
    if (!amount && amount !== 0) return '$0.00'
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(amount)
  },

  // Date formatting
  formatDate(date, format = 'short', locale = 'en-US') {
    if (!date) return ''
    const dateObj = new Date(date)

    const options = {
      short: { year: 'numeric', month: 'short', day: 'numeric' },
      long: { year: 'numeric', month: 'long', day: 'numeric' },
      datetime: {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    }

    return new Intl.DateTimeFormat(locale, options[format]).format(dateObj)
  },

  // Relative time formatting
  formatRelativeTime(date) {
    if (!date) return ''
    const now = new Date()
    const diffInSeconds = Math.floor((now - new Date(date)) / 1000)

    if (diffInSeconds < 60) return 'just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`

    return this.formatDate(date, 'short')
  },

  // File size formatting
  formatFileSize(bytes) {
    if (!bytes) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  },

  // Percentage formatting
  formatPercentage(value, decimals = 1) {
    if (!value && value !== 0) return '0%'
    return `${(value * 100).toFixed(decimals)}%`
  }
}

export const validators = {
  // Email validation
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  },

  // Phone validation
  isValidPhone(phone) {
    const phoneRegex = /^\+?[\d\s-()]+$/
    return phoneRegex.test(phone)
  },

  // URL validation
  isValidUrl(url) {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  },

  // Password strength
  getPasswordStrength(password) {
    let strength = 0
    if (password.length >= 8) strength++
    if (/[A-Z]/.test(password)) strength++
    if (/[a-z]/.test(password)) strength++
    if (/[0-9]/.test(password)) strength++
    if (/[^A-Za-z0-9]/.test(password)) strength++

    const levels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong']
    return { score: strength, level: levels[strength] || 'Very Weak' }
  },

  // Required field validation
  required(value, message = 'This field is required') {
    return value && value.toString().trim() !== '' ? null : message
  },

  // Minimum length validation
  minLength(value, min, message) {
    if (!value) return null
    return value.length >= min ? null : message || `Minimum ${min} characters required`
  },

  // Maximum length validation
  maxLength(value, max, message) {
    if (!value) return null
    return value.length <= max ? null : message || `Maximum ${max} characters allowed`
  }
}

export const helpers = {
  // Debounce function
  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  },

  // Throttle function
  throttle(func, limit) {
    let inThrottle
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  },

  // Deep clone object
  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj))
  },

  // Generate random ID
  generateId() {
    return Math.random().toString(36).substr(2, 9)
  },

  // Capitalize first letter
  capitalize(str) {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1)
  },

  // Truncate text
  truncate(text, length = 100, suffix = '...') {
    if (!text) return ''
    if (text.length <= length) return text
    return text.substring(0, length) + suffix
  },

  // Check if object is empty
  isEmpty(obj) {
    return Object.keys(obj).length === 0
  },

  // Sort array by property
  sortBy(array, property, direction = 'asc') {
    return array.sort((a, b) => {
      const aVal = a[property]
      const bVal = b[property]

      if (direction === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0
      }
    })
  }
}

export const countries = {
  // Comprehensive list of international country codes
  internationalCountryCodes: 'AF,AX,AL,DZ,AS,AD,AO,AI,AQ,AG,AR,AM,AW,AU,AT,AZ,BS,BH,BD,BB,BY,BE,BZ,BJ,BM,BT,BO,BQ,BA,BW,BV,BR,IO,BN,BG,BF,BI,CV,KH,CM,CA,KY,CF,TD,CL,CN,CX,CC,CO,KM,CG,CD,CK,CR,CI,HR,CU,CW,CY,CZ,DK,DJ,DM,DO,EC,EG,SV,GQ,ER,EE,SZ,ET,FK,FO,FJ,FI,FR,GF,PF,TF,GA,GM,GE,DE,GH,GI,GR,GL,GD,GP,GU,GT,GG,GN,GW,GY,HT,HM,VA,HN,HK,HU,IS,IN,ID,IR,IQ,IE,IM,IL,IT,JM,JP,JE,JO,KZ,KE,KI,KP,KR,KW,KG,LA,LV,LB,LS,LR,LY,LI,LT,LU,MO,MG,MW,MY,MV,ML,MT,MH,MQ,MR,MU,YT,MX,FM,MD,MC,MN,ME,MS,MA,MZ,MM,NA,NR,NP,NL,NC,NZ,NI,NE,NG,NU,NF,MK,MP,NO,OM,PK,PW,PS,PA,PG,PY,PE,PH,PN,PL,PT,PR,QA,RE,RO,RU,RW,BL,SH,KN,LC,MF,PM,VC,WS,SM,ST,SA,SN,RS,SC,SL,SG,SX,SK,SI,SB,SO,ZA,GS,SS,ES,LK,SD,SR,SJ,SE,CH,SY,TW,TJ,TZ,TH,TL,TG,TK,TO,TT,TN,TR,TM,TC,TV,UG,UA,AE,GB,UM,US,UY,UZ,VU,VE,VN,VG,VI,WF,EH,YE,ZM,ZW'.split(','),

  // Get formatted country list with names using Intl API
  getCountries() {
    const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
    return this.internationalCountryCodes
      .map((value) => ({ value, label: regionNames.of(value) || value }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }
}
